import { useSuspenseQuery, UseSuspenseQueryResult, UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { paginatedCommunityResponseSchema, CommunitiesSearch, PaginatedCommunityResponse, CommunityType } from './community.types';
import { mutationResponseSchema, MutationResponse } from '../shared.types';

const SEARCH_QUERY_KEY = 'CommunitiesSearch';

const searchCommunities = async (searchRequest: CommunitiesSearch): Promise<PaginatedCommunityResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}community/search`, {
            json: searchRequest,
        })
        .json();
    return paginatedCommunityResponseSchema.parse(response);
};

export const useSearchCommunities = (searchRequest: CommunitiesSearch): UseSuspenseQueryResult<PaginatedCommunityResponse> => {
    return useSuspenseQuery<PaginatedCommunityResponse>({
        queryKey: [SEARCH_QUERY_KEY, searchRequest],
        queryFn: () => searchCommunities(searchRequest),
    });
};

const saveCommunity = async (community: CommunityType): Promise<MutationResponse> => {
    let response;
    if (community.id && community.id > 0) {
        // Existing community - update
        response = await createPacerApi()
            .put(`${API_ROOT}community/update`, {
                json: community,
            })
            .json();
    } else {
        // New community - create
        response = await createPacerApi()
            .post(`${API_ROOT}community/create`, {
                json: community,
            })
            .json();
    }
    return mutationResponseSchema.parse(response);
};

export const useSaveCommunity = (callback?: (results: MutationResponse) => void): UseMutationResult<MutationResponse, Error, CommunityType> => {
    const queryClient = useQueryClient();
    return useMutation<MutationResponse, Error, CommunityType>({
        mutationFn: saveCommunity,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);
            queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
        },
    });
};
